import { useEffect, useMemo, useState } from "react"
import { DataType } from "../../../../../tables"
import { FieldType, FieldValue, FilterField, Filters, O, option, TimePeriod, timePeriodOptions } from "../../../../../features/ui"
import { useTransactionHooks } from "../../../hooks/transactionHooks"
import { useCashFundLoaders } from "../../../loaders/cashFundLoaders"
import { TransactionType } from "./types"

type Props = {
    onChangeCashFund: (cashFundId?: string) => void
    onSearchStart: () => void
    onSearchDone: (dataTypes: DataType[]) => void
}

const FiltersKey = 'transaction'

export const TransactionFilters = (props: Props) => {
    const { onSearchStart, onSearchDone } = props

    const buildDateFrom = (daysBack: number) => {
        const newDateFrom = new Date()
        newDateFrom.setDate(newDateFrom.getDate() - daysBack)
        newDateFrom.setHours(0, 0, 0, 0)
        return newDateFrom
    }

    const buildDateTo = () => {
        const newDateTo = new Date()
        newDateTo.setHours(23, 59, 59, 999)
        return newDateTo
    }

    const [searchText, setSearchText] = useState<string>()
    const [period, setPeriod] = useState<string>(TimePeriod.LAST_7_DAYS)
    const [dateFrom, setDateFrom] = useState<Date>(buildDateFrom(7))
    const [dateTo, setDateTo] = useState<Date>(buildDateTo())
    const [cashFundId, setCashFundId] = useState<string>()
    const [transactionType, setTransactionType] = useState<O<TransactionType>>(TransactionType.ALL)
    const [amountFrom, setAmountFrom] = useState<number>()
    const [amountTo, setAmountTo] = useState<number>()

    const { searchTransactions } = useTransactionHooks()

    const cashFundLoaders = useCashFundLoaders()

    useEffect(() => {
        search(dateFrom, dateTo)
    }, [])

    useEffect(() => {
        props.onChangeCashFund(cashFundId)
    }, [cashFundId])

    const cashFundOptions = useMemo(() =>
        cashFundLoaders().findAllCashFunds(true).map(cashFund => option(cashFund.id, cashFund.name)
    ), [])

    const transactionTypeOptions = useMemo(() => [
        option(TransactionType.ALL, 'Todos'),
        option(TransactionType.INCOME, 'Ingresos'),
        option(TransactionType.OUTCOME, 'Egresos')
    ], [])

    const search = async (
        dateFromParam: Date,
        dateToParam: Date,
        searchTextParam?: string,
        cashFundIdParam?: string,
        transactionTypeParam?: TransactionType,
        amountFromParam?: number,
        amountToParam?: number
    ) => {
        onSearchStart()
        searchTransactions(searchTextParam, dateFromParam, dateToParam, cashFundIdParam, transactionTypeParam, amountFromParam, amountToParam).then(searchedList => {
            setTimeout(() => onSearchDone(searchedList), 100)
        })
    }

    const handleSearch = (value?: FieldValue) => {
        const newSearchText = value as O<string>
        setSearchText(newSearchText)
        search(dateFrom, dateTo, newSearchText, cashFundId, transactionType, amountFrom, amountTo)
    }

    const onChangePeriod = (value?: FieldValue) => {
        const newPeriod = value as O<string>
        if (newPeriod) {
            setPeriod(newPeriod)
            if (newPeriod !== TimePeriod.MANUAL) {
                let newDateFrom = dateFrom
                if (newPeriod === TimePeriod.TODAY) {
                    newDateFrom = buildDateFrom(0)
                } else if (value === TimePeriod.LAST_7_DAYS) {
                    newDateFrom = buildDateFrom(7)
                } else if (value === TimePeriod.LAST_14_DAYS) {
                    newDateFrom = buildDateFrom(14)
                } else if (value === TimePeriod.LAST_30_DAYS) {
                    newDateFrom = buildDateFrom(30)
                }
                const newDateTo = buildDateTo()
                setDateFrom(newDateFrom)
                setDateTo(newDateTo)
                search(newDateFrom, newDateTo, searchText, cashFundId, transactionType, amountFrom, amountTo)
            }
        }
    }

    const onChangeDateFrom = (value?: FieldValue) => {
        const newDateFrom = value as O<Date>
        newDateFrom?.setHours(0, 0, 0, 0)
        if (newDateFrom) {
            setPeriod(TimePeriod.MANUAL)
            setDateFrom(newDateFrom)
            search(newDateFrom, dateTo, searchText, cashFundId, transactionType, amountFrom, amountTo)
        }
    }
    
    const onChangeDateTo = (value?: FieldValue) => {
        const newDateTo = value as O<Date>
        newDateTo?.setHours(23, 59, 59, 999)
        if (newDateTo) {
            setPeriod(TimePeriod.MANUAL)
            setDateTo(newDateTo)
            search(dateFrom, newDateTo, searchText, cashFundId, transactionType, amountFrom, amountTo)
        }
    }

    const onChangeCashFund = (value?: FieldValue) => {
        const newCashFundId = value as O<string>
        setCashFundId(newCashFundId)
        search(dateFrom, dateTo, searchText, newCashFundId, transactionType, amountFrom, amountTo)
    }

    const onChangeTransactionType = (value?: FieldValue) => {
        const newTransactionType = value as O<TransactionType>
        setTransactionType(newTransactionType)
        search(dateFrom, dateTo, searchText, cashFundId, newTransactionType, amountFrom, amountTo)
    }

    const onChangeAmountFrom = (value?: FieldValue) => {
        const newAmountFrom = value as O<number>
        setAmountFrom(newAmountFrom)
        search(dateFrom, dateTo, searchText, cashFundId, transactionType, newAmountFrom, amountTo)
    }

    const onChangeAmountTo = (value?: FieldValue) => {
        const newAmountTo = value as O<number>
        setAmountTo(newAmountTo)
        search(dateFrom, dateTo, searchText, cashFundId, transactionType, amountFrom, newAmountTo)
    }

    const searchField: FilterField = {
        name: 'search',
        type: FieldType.TEXT,
        placeholder: 'Buscar por código',
        icon: 'search',
        onChange: handleSearch
    }

    const filterFields: FilterField[] = [
        {
            name: 'period',
            type: FieldType.SELECT,
            value: period,
            options: timePeriodOptions(),
            label: 'Período',
            space: 2,
            onChange: onChangePeriod
        },
        {
            name: 'dateFrom',
            type: FieldType.DATE,
            value: dateFrom,
            label: 'Fecha: Desde',
            max: dateTo,
            space: 2,
            onChange: onChangeDateFrom
        },
        {
            name: 'dateTo',
            type: FieldType.DATE,
            value: dateTo,
            label: 'Fecha: Hasta',
            min: dateFrom,
            max: new Date(),
            space: 2,
            onChange: onChangeDateTo
        },
        {
            name: 'cashFund',
            type: FieldType.SELECT,
            label: 'Caja',
            options: cashFundOptions,
            space: 3,
            onChange: onChangeCashFund
        },
        {
            name: 'transactionType',
            type: FieldType.SELECT,
            label: 'Ingresos / Egresos',
            options: transactionTypeOptions,
            defaultValue: transactionType,
            space: 2,
            onChange: onChangeTransactionType
        },
        {
            name: 'amountFrom',
            type: FieldType.NUMBER,
            label: 'Monto: Desde',
            max: amountTo,
            space: 2,
            onChange: onChangeAmountFrom
        },
        {
            name: 'amountTo',
            type: FieldType.NUMBER,
            label: 'Monto: Hasta',
            min: amountFrom,
            space: 2,
            onChange: onChangeAmountTo
        }
    ]

    return (
        <Filters
            filtersKey={FiltersKey}
            searchField={searchField}
            fields={filterFields}
            firstRowLength={3}
        />
    )
}
